/**
 * Mobile Data Fetching Hook
 * Extracted from CreateTransactionMobile.tsx
 * 
 * Handles data fetching for mobile transaction form:
 * - Accounts
 * - Currencies
 * - Categories (expense/income)
 * - Category groups
 * - Recent transactions (for autofill)
 * - Workspace settings (default currency)
 */

import { useEffect, useState, useRef } from "react";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  getDocs,
  doc,
  getDoc,
  Unsubscribe,
  FirestoreError,
} from "firebase/firestore";
import { db } from "../../../../firebase";
import { Account, Currency, Transaction, TransactionCategory, TransactionCategoryGroup, WorkspaceSettings } from "../../../../types";
import { api } from "../../../../services/api";
import { sortCurrenciesByOrder } from "../../../../utils/currencyOrdering";
import { Timestamp } from "firebase/firestore";

export interface UseMobileDataFetchingResult {
  accounts: Account[];
  currencies: Currency[];
  defaultCurrency: string;
  expenseCategories: TransactionCategory[];
  incomeCategories: TransactionCategory[];
  categoryGroups: TransactionCategoryGroup[];
  transactions: Transaction[];
  loading: boolean;
  workspaceMembersCount: number;
  /** Transaction ID prefix for header (e.g. "DV"); from same settings doc as defaultCurrency so available when form is shown. */
  transactionIdPrefix: string;
  /** Next sequence for new transactions (for "next ID" in create mode). */
  transactionIdNextSequence: number | undefined;
}

export interface UseMobileDataFetchingParams {
  workspaceId: string;
  transactionType: "expense" | "income" | "transfer";
}

/**
 * Hook for fetching all data needed for mobile transaction form
 */
export const useMobileDataFetching = ({
  workspaceId,
  transactionType,
}: UseMobileDataFetchingParams): UseMobileDataFetchingResult => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [defaultCurrency, setDefaultCurrency] = useState<string>("USD");
  const [currencyOrder, setCurrencyOrder] = useState<string[]>([]);
  const defaultCurrencyRef = useRef<string>("USD");
  const currencyOrderRef = useRef<string[]>([]);
  const [expenseCategories, setExpenseCategories] = useState<TransactionCategory[]>([]);
  const [incomeCategories, setIncomeCategories] = useState<TransactionCategory[]>([]);
  const [categoryGroups, setCategoryGroups] = useState<TransactionCategoryGroup[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [workspaceMembersCount, setWorkspaceMembersCount] = useState<number>(0);
  const [transactionIdPrefix, setTransactionIdPrefix] = useState<string>("");
  const [transactionIdNextSequence, setTransactionIdNextSequence] = useState<number | undefined>(undefined);

  const hasReceivedSettingsRef = useRef(false);
  const hasReceivedAccountsRef = useRef(false);
  const hasReceivedCategoryGroupsRef = useRef(false);
  const prevWorkspaceIdRef = useRef<string | null>(null);

  // Type guard helper
  const isExpenseOrIncome = (t: typeof transactionType): t is "expense" | "income" => {
    return t === "expense" || t === "income";
  };

  useEffect(() => {
    if (!workspaceId) return;

    // Only show full-screen loading when workspace changed (initial load); not when only switching type (expense/income/transfer)
    const isWorkspaceChange = prevWorkspaceIdRef.current !== workspaceId;
    if (isWorkspaceChange) {
      prevWorkspaceIdRef.current = workspaceId;
      setLoading(true);
      hasReceivedSettingsRef.current = false;
      hasReceivedAccountsRef.current = false;
      hasReceivedCategoryGroupsRef.current = false;
    }

    const trySetLoadingFalse = () => {
      if (!hasReceivedSettingsRef.current) return;
      if (transactionType === "transfer") {
        if (hasReceivedAccountsRef.current) setLoading(false);
      } else {
        if (hasReceivedCategoryGroupsRef.current) setLoading(false);
      }
    };

    // Load accounts
    const accountsRef = collection(db, "workspaces", workspaceId, "accounts");
    const unsubscribeAccounts = onSnapshot(accountsRef, (snapshot) => {
      const accountsData: Account[] = snapshot.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        } as Account))
        .filter((acc) => !acc.isArchived);
      setAccounts(accountsData);

      if (transactionType === "transfer") {
        hasReceivedAccountsRef.current = true;
        trySetLoadingFalse();
      }
    });

    // Load currencies
    const currenciesRef = collection(db, "workspaces", workspaceId, "currencies");
    const unsubscribeCurrencies = onSnapshot(currenciesRef, (snapshot) => {
      const currentDefault = defaultCurrencyRef.current;
      const currentOrder = currencyOrderRef.current;
      const currenciesList: Currency[] = snapshot.docs.map((doc) => {
        const data = doc.data();
        // Use doc.id as code if code is not set (doc.id should be the currency code)
        const currencyCode = (data.code as string) || doc.id;
        
        // Get rate for current default currency
        const rates = (data.rates as Record<string, number | null>) || {};
        let currentRate: number | null;
        if (rates[currentDefault] !== undefined) {
          currentRate = rates[currentDefault];
        } else if (typeof data.rate === "number") {
          currentRate = data.rate;
        } else if (data.rate === null || data.rate === undefined) {
          currentRate = null;
        } else {
          const parsed = parseFloat(String(data.rate));
          currentRate = isNaN(parsed) ? null : parsed;
        }
        
        return {
          id: doc.id,
          code: currencyCode,
          name: (data.name as string) || "",
          symbol: (data.symbol as string) || "",
          rate: currentRate,
          rates: rates,
          decimalPlaces: data.decimalPlaces as number | undefined,
          updatedAt: data.updatedAt || data.createdAt || Timestamp.now(),
        } as Currency;
      });
      
      // Keep default currency in list so validation/formatting get its decimalPlaces (e.g. AMD with 2 decimals)
      const sortedCurrencies = sortCurrenciesByOrder(currenciesList, currentOrder, currentDefault);
      setCurrencies(sortedCurrencies);
    });

    // Load workspace settings to get default currency and currency order
    // NOTE: defaultCurrency is stored in workspaces/{workspaceId}/settings/general, not in the workspace document itself
    const settingsRef = doc(db, "workspaces", workspaceId, "settings", "general");
    const unsubscribeSettings = onSnapshot(settingsRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data() as WorkspaceSettings;
        const newDefaultCurrency = data.defaultCurrency || "USD";
        const newCurrencyOrder = data.currencyOrder || [];
        defaultCurrencyRef.current = newDefaultCurrency;
        currencyOrderRef.current = newCurrencyOrder;
        setDefaultCurrency(newDefaultCurrency);
        setCurrencyOrder(newCurrencyOrder);
        const prefix = data.transactionIdPrefix ?? "";
        setTransactionIdPrefix(prefix);
        setTransactionIdNextSequence(
          prefix && typeof prefix === "string" && prefix.trim() !== ""
            ? (typeof data?.transactionIdNextSequence === "number" ? data.transactionIdNextSequence : 1)
            : undefined
        );
      } else {
        defaultCurrencyRef.current = "USD";
        currencyOrderRef.current = [];
        setDefaultCurrency("USD");
        setCurrencyOrder([]);
        setTransactionIdPrefix("");
        setTransactionIdNextSequence(undefined);
      }
      hasReceivedSettingsRef.current = true;
      trySetLoadingFalse();
    }, (error) => {
      console.error("Error loading default currency:", error);
      defaultCurrencyRef.current = "USD";
      currencyOrderRef.current = [];
      setDefaultCurrency("USD");
      setCurrencyOrder([]);
      setTransactionIdPrefix("");
      setTransactionIdNextSequence(undefined);
      hasReceivedSettingsRef.current = true;
      trySetLoadingFalse();
    });

    // Load workspace members count (for showing transaction ID in edit mode when multiple users)
    const membersRef = collection(db, "workspaces", workspaceId, "members");
    const unsubscribeMembers = onSnapshot(membersRef, (snapshot) => {
      setWorkspaceMembersCount(snapshot.docs.length);
    });

    // Set up real-time listeners for categories and groups
    if (isExpenseOrIncome(transactionType)) {
      // Real-time listener for expense categories
      const expenseCategoriesRef = collection(db, "workspaces", workspaceId, "categories");
      const expenseCategoriesQuery = query(expenseCategoriesRef, where("type", "==", "expense"));
      const unsubscribeExpenseCategories = onSnapshot(expenseCategoriesQuery, (snapshot) => {
        const expenseCats: TransactionCategory[] = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            name: data.name as string,
            type: "expense" as const,
            groupId: data.groupId as string,
            enabledDetails: data.enabledDetails as string[] | undefined,
            defaultValues: data.defaultValues as Record<string, string | number> | undefined,
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
            order: data.order as number | undefined,
          };
        });
        setExpenseCategories(expenseCats);
      });

      // Real-time listener for income categories
      const incomeCategoriesQuery = query(expenseCategoriesRef, where("type", "==", "income"));
      const unsubscribeIncomeCategories = onSnapshot(incomeCategoriesQuery, (snapshot) => {
        const incomeCats: TransactionCategory[] = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            name: data.name as string,
            type: "income" as const,
            groupId: data.groupId as string,
            enabledDetails: data.enabledDetails as string[] | undefined,
            defaultValues: data.defaultValues as Record<string, string | number> | undefined,
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
            order: data.order as number | undefined,
          };
        });
        setIncomeCategories(incomeCats);
      });

      // Real-time listener for expense groups
      const expenseGroupsRef = collection(db, "workspaces", workspaceId, "categoryGroups");
      const expenseGroupsQuery = query(expenseGroupsRef, where("type", "==", "expense"));
      const unsubscribeExpenseGroups = onSnapshot(expenseGroupsQuery, (snapshot) => {
        const expenseGroups: TransactionCategoryGroup[] = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            name: data.name as string,
            type: "expense" as const,
            description: data.description as string | undefined,
            details: Array.isArray(data.details) ? data.details : [],
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
          };
        });

        // Update category groups (merge with income groups)
        setCategoryGroups((prevGroups) => {
          const incomeGroups = prevGroups.filter((g) => g.type === "income");
          const newGroups = [...expenseGroups, ...incomeGroups];

          if (newGroups.length > 0) {
            hasReceivedCategoryGroupsRef.current = true;
            trySetLoadingFalse();
          }

          return newGroups;
        });
      });

      // Real-time listener for income groups
      const incomeGroupsQuery = query(expenseGroupsRef, where("type", "==", "income"));
      const unsubscribeIncomeGroups = onSnapshot(incomeGroupsQuery, (snapshot) => {
        const incomeGroups: TransactionCategoryGroup[] = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            name: data.name as string,
            type: "income" as const,
            description: data.description as string | undefined,
            details: Array.isArray(data.details) ? data.details : [],
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
          };
        });

        // Update category groups (merge with expense groups)
        setCategoryGroups((prevGroups) => {
          const expenseGroups = prevGroups.filter((g) => g.type === "expense");
          const newGroups = [...expenseGroups, ...incomeGroups];

          if (newGroups.length > 0) {
            hasReceivedCategoryGroupsRef.current = true;
            trySetLoadingFalse();
          }

          return newGroups;
        });
      });

      // Load recent transactions to get previous measurable values for autofill
      const loadTransactions = async () => {
        try {
          const transactionsRef = collection(db, "workspaces", workspaceId, "transactions");
          // Only load transactions matching the current transaction type
          if (isExpenseOrIncome(transactionType)) {
            const q = query(
              transactionsRef,
              where("type", "==", transactionType),
              orderBy("date", "desc"),
              limit(100) // Load last 100 transactions to find previous measurable values
            );
            const snapshot = await getDocs(q);
            const transactionsData = snapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            } as Transaction));
            setTransactions(transactionsData);
          }
        } catch (error) {
          console.error("Error loading transactions:", error);
          // If query fails (e.g., missing index), try loading without filter
          try {
            const transactionsRef = collection(db, "workspaces", workspaceId, "transactions");
            const snapshot = await getDocs(transactionsRef);
            const allTransactions = snapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            } as Transaction));
            // Filter on client side
            const filtered = allTransactions
              .filter((t) => t.type === transactionType)
              .sort((a, b) => {
                let dateA: Date;
                if ((a.date as any)?.toDate) {
                  dateA = (a.date as any).toDate();
                } else if (a.date instanceof Date) {
                  dateA = a.date;
                } else {
                  dateA = new Date(a.date as unknown as string);
                }

                let dateB: Date;
                if ((b.date as any)?.toDate) {
                  dateB = (b.date as any).toDate();
                } else if (b.date instanceof Date) {
                  dateB = b.date;
                } else {
                  dateB = new Date(b.date as unknown as string);
                }

                return dateB.getTime() - dateA.getTime();
              })
              .slice(0, 100);
            setTransactions(filtered);
          } catch (fallbackError) {
            console.error("Error loading transactions (fallback):", fallbackError);
          }
        }
      };
      loadTransactions();

      // Cleanup listeners on unmount
      return () => {
        unsubscribeExpenseCategories();
        unsubscribeIncomeCategories();
        unsubscribeExpenseGroups();
        unsubscribeIncomeGroups();
      };
    }

    // Loading is set false when settings + (accounts for transfer OR category groups for expense/income) are ready.
    // Fallback: after 5s force loading false so the user is never stuck.
    const fallbackTimeout = setTimeout(() => {
      setLoading(false);
    }, 5000);

    return () => {
      clearTimeout(fallbackTimeout);
      unsubscribeAccounts();
      unsubscribeCurrencies();
      unsubscribeSettings();
      unsubscribeMembers();
    };
  }, [workspaceId, transactionType]);

  return {
    accounts,
    currencies,
    defaultCurrency,
    expenseCategories,
    incomeCategories,
    categoryGroups,
    transactions,
    loading,
    workspaceMembersCount,
    transactionIdPrefix,
    transactionIdNextSequence,
  };
};
